import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  CouponsAdminGetResponse,
  CouponPostRequest,
  CouponPostResponse,
  DiscountCodesGetResponse,
  DiscountCodePostRequest,
  DiscountCodePostResponse,
  DiscountCodePatchRequest,
  DiscountCodePatchResponse,
  ProductsGetResponse,
} from "../../../api/src/admin/discounts/route.js";
import { prepareHeaders } from "./utils.js";
import config from "../config.js";

export const discountsApi = createApi({
  reducerPath: "discountsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["Coupon", "DiscountCode", "Product"],
  endpoints: (builder) => ({
    getCoupons: builder.query<CouponsAdminGetResponse, void>({
      query: () => "/admin/discounts/coupons",
      providesTags: ["Coupon"],
    }),
    createCoupon: builder.mutation<CouponPostResponse, CouponPostRequest>({
      query: (couponData) => ({
        url: "/admin/discounts/coupons",
        method: "POST",
        body: couponData,
      }),
      invalidatesTags: ["Coupon"],
    }),
    getDiscountCodes: builder.query<DiscountCodesGetResponse, string>({
      query: (couponId) =>
        `/admin/discounts/coupons/${couponId}/discount-codes`,
      providesTags: ["DiscountCode"],
    }),
    createDiscountCode: builder.mutation<
      DiscountCodePostResponse,
      { couponId: string; data: DiscountCodePostRequest }
    >({
      query: ({ couponId, data }) => ({
        url: `/admin/discounts/coupons/${couponId}/discount-codes`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["DiscountCode", "Coupon"],
    }),
    updateDiscountCode: builder.mutation<
      DiscountCodePatchResponse,
      { discountCodeId: string; data: DiscountCodePatchRequest }
    >({
      query: ({ discountCodeId, data }) => ({
        url: `/admin/discounts/discount-codes/${discountCodeId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["DiscountCode", "Coupon"],
    }),
    getProducts: builder.query<ProductsGetResponse, void>({
      query: () => "/admin/discounts/products",
      providesTags: ["Product"],
    }),
  }),
});

export const {
  useGetCouponsQuery,
  useCreateCouponMutation,
  useGetDiscountCodesQuery,
  useCreateDiscountCodeMutation,
  useUpdateDiscountCodeMutation,
  useGetProductsQuery,
} = discountsApi;
